import React, { useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import MyAccountContentHeader from '../MyAccountContentHeader/MyAccountContentHeader';
import MyAccountOrderCard from '../MyAccountOrderCard/MyAccountOrderCard';
import * as translator from '../../../../utils/translator';
import './MyAccountMyOrders.css';
import MyAccountFilters from '../MyAccountFilters/MyAccountFilters';
import AppInstallBanner from '../../../../components/AppInstallBanner/AppInstallBanner';
import NoOrdersDisplay from '../NoOrdersDisplay/NoOrdersDisplay';
import Pagination from '../../../../components/Pagination/Pagination';
import * as http from '../../../../utils/http';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import { assignWlReorderVariant, WlReorderVariant } from '../../../../utils/wlReorderSubscriptionExperiment';
import { trackReorderWlTest } from '../../../Reorder/ReorderTrackingManager';

const defaultImage = require('../../../../images/product_box.jpg') as string | undefined;

type MyOrders = {
	notifications: number;
	orders: MyAccountOrder[];
	totalOrders: number;
	otherCountriesOrders: Record<string, { count: number; language: string }>;
};

const MyAccountMyOrders = () => {
	const { setActiveMenuLinkId, country, language, ordersStatuses, ordersProducts, setError, refreshData, setLoading, asideBarData } = useOutletContext<any>();
	const [searchParams] = useSearchParams();

	const [myOrders, setMyOrders] = useState<MyOrders>({
		orders: [],
		totalOrders: 0,
		otherCountriesOrders: {},
		notifications: 0,
	});
	// WL reorder test arm; null until an eligible order is shown.
	const [wlReorderVariant, setWlReorderVariant] = useState<WlReorderVariant | null>(null);
	// Once per visit: refetches (page, filter, refreshData) reuse the mount.
	const wlReorderTestTracked = useRef(false);

	// Bucket before the cards render, so the B CTA is there on the first paint.
	const assignWlReorderTest = (orders: MyAccountOrder[] | undefined) => {
		const offer = orders?.find((order) => order.wlSubscriptionOffer)?.wlSubscriptionOffer;
		if (!offer) return;
		const variant = assignWlReorderVariant(offer.testWeights);
		setWlReorderVariant(variant);
		if (!wlReorderTestTracked.current) {
			wlReorderTestTracked.current = true;
			trackReorderWlTest(variant);
		}
	};


	// Calculate paginated orders

	const loadOrders = () => {
		setLoading(true);

		http
			.getFromServer(`${country}/account/orders?${searchParams.toString()}`, [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER])
			.then((response) => {
				assignWlReorderTest(response.data?.orders);
				setMyOrders(response.data);
			})
			.catch((error) => {
				window.clientLogger.error('Error in MyAccountMyOrders: ', error);
				setError(500);
			})
			.finally(() => {
				setLoading(false);
			});
	}

	useEffect(() => {
		//On lending and having CTA in the url query params then send event to mixpanel
		const hashIdCTAKey = "last_email_order_id_cta";//local storage key
		//This is order_hash_id + ctaType
		const lasteEmailFromStorage = localStorage.getItem(hashIdCTAKey);//hash ID + '&' ctaType
		const lasteEmailFromStorageArray = lasteEmailFromStorage?.split("&");
		
		const storageOrderHashID = lasteEmailFromStorageArray?.[0];
		const storageCTA = lasteEmailFromStorageArray?.[1];

		const ctaType = searchParams.get("CTA_type");
		const order_hash_id =searchParams.get("order_hash_id") ?? "url param is missing";
		if (ctaType && (storageOrderHashID !== order_hash_id || storageCTA !== ctaType)) {
			const emailParams = {
				CTA_type: ctaType,
				country: searchParams.get("country") ?? "url param is missing",
				language: searchParams.get("language") ?? "url param is missing",
				order_hash_id: order_hash_id,
			}
			//remember this order id to avoid sending the event again.
			localStorage.setItem(hashIdCTAKey, order_hash_id.concat("&", ctaType));
			//send event to mixpanel
			MainTracker.track("custom", "Cart abandonment CTA clicked", emailParams);
		}
	}, []);

	useEffect(() => {
		setActiveMenuLinkId('my-orders');
		loadOrders();
	}, [refreshData, searchParams]);


	if (!myOrders) {
		return <></>;
	}

	return (
		<>
			<div className="account-content">
				<MyAccountContentHeader
					title={translator.translate(language, 'my_account', 'MyOrders/title')}
					description={translator.translate(language, 'my_account', 'MyOrders/subTitle')}
					showPrintOptionInDescription={myOrders?.orders?.length > 0}
					showOtherOrders
					otherCountriesOrders={myOrders?.otherCountriesOrders}
				/>
				<MyAccountFilters country={country} orderArray={myOrders.orders || []} ordersStatuses={ordersStatuses || []} ordersProducts={ordersProducts || []} totalOrders={myOrders?.totalOrders ?? 0} hasCarePlus={Boolean(asideBarData?.hasActiveCarePlusSubscription)} />
        

				{/* Only with orders: the copy says the last order is already stored, wrong above the empty state */}
				{myOrders?.orders?.length > 0 && <AppInstallBanner
					desktopPlacementId="W3"
					mobilePlacementId="W4"
					language={language}
					country={country}
					customerUuid={asideBarData?.uuid}
					isPairedToMobileApp={asideBarData?.isPairedToMobileApp}
					allowedPlacements={asideBarData?.appInstallPlacements}
					className="account-app-install-banner"
				/>}

				{myOrders?.orders?.map((order: any, index: number) => {
					return <MyAccountOrderCard key={index} defaultImage={defaultImage} order={order} wlReorderVariant={wlReorderVariant} />;
				})}

				{(myOrders?.orders?.length === 0 || !myOrders?.orders) && <NoOrdersDisplay />}


				<Pagination 
					totalItems={myOrders?.totalOrders ?? 0}
				/>
			</div>
		</>
	);
};

export default MyAccountMyOrders;